import { ApiError } from "@api/errors";
import i18next, { i18nReady } from "@i18n";
import { MutationCache, QueryClient, useQueryClient } from "@tanstack/react-query";
import { notifyError } from "@ui/notify";
import { useCallback } from "react";

declare module "@tanstack/react-query" {
  interface Register {
    /** ``inlineError``: the screen shows this write's failure itself (no toast); ``toastId``: one toast however many
     *  of these writes fail together. */
    mutationMeta: { inlineError?: boolean; toastId?: string };
  }
}

export const queryClient = new QueryClient({
  // every failed write says why in a red toast (a write's own onError only undoes what it showed early), unless the
  // screen shows the failure inline
  mutationCache: new MutationCache({
    onError: (error, _vars, _context, mutation) => {
      if (!mutation.meta?.inlineError) notifyError(error, mutation.meta?.toastId);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // 4xx answers are final; retry only network hiccups and 5xx
      retry: (count, error) => count < 2 && !(error instanceof ApiError && error.status < 500),
    },
  },
});

// server texts (labels, document and job titles) come in the UI language: a switch fetches what is shown again
// (after the first language is in: its own start is no switch)
void i18nReady.then(() => i18next.on("languageChanged", () => void queryClient.invalidateQueries()));

/** A write's follow-up: the queries whose path matches ``paths`` (a module constant) are read again. */
export function useInvalidate(paths: RegExp) {
  const client = useQueryClient();
  return useCallback(() => void client.invalidateQueries({ predicate: (q) => paths.test(String(q.queryKey[0])) }), [client, paths]);
}

/** A read whose answer depends only on its request (an analysis, a report): fetched once, never retried. */
export const ONCE = { staleTime: Number.POSITIVE_INFINITY, retry: false } as const;
